/// <reference path="../../css.d.ts" />
"use client";
// Atelier NIT : libellés français, imports compatibles React 19.1, tokens de charte partagés.
// @ubm/ui/components/textarea — LA zone de texte canonique (lot U1, 05/10/2026).
// Source : UIArc `registry/components/textarea/textarea.tsx` @ 7238f98196b147a8d50ce467abcbad310d4d8414 (MIT) — messages animés,
// anneau de focus qui se resserre sur la bordure, peinture du module, mouvement réduit, repris à l'identique.
// Moteur unique : l'ancien `primitives/Textarea` est DÉPLACÉ ici ; `@ubm/ui` Textarea en est la façade (réexport). Vrai
// <textarea> HTML : ref, name, value/defaultValue, FormData, reset, événements transmis. `label` / `description` / `error`
// FACULTATIFS (sans eux, AUCUN DOM ajouté). Classes historiques conservées ; peinture effective du module (hors couche).

import { forwardRef, useEffect, useId, useRef, useState } from "react";
import type { ReactNode, TextareaHTMLAttributes } from "react";
import { AnimatePresence, motion, useReducedMotionConfig as useReducedMotion } from "motion/react";

const cn = (...values: (string | undefined)[]) => values.filter(Boolean).join(" ");
import { motionTokens } from "../../lib/motion-tokens";
// Fondation UIArc (jetons --arc-*) embarquée par le composant : aucun import d'app ni fournisseur requis.
// Shared lab theme supplies foundation aliases.
import styles from "./textarea.module.css";

const textareaVariants = ({size = "md"}: {size?: "xs"|"sm"|"md"|"lg"|"xl"}) => `ubm-ui-field ubm-ui-textarea size-${size}`;

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  size?: "xs"|"sm"|"md"|"lg"|"xl";
  /** Libellé visible (API UIArc). Absent : aucun DOM ajouté. */
  label?: ReactNode;
  /** Aide animée (API UIArc), reliée par `aria-describedby`. */
  description?: string;
  /** Erreur animée (API UIArc, `role="alert"`) : pose aussi `aria-invalid`. */
  error?: string;
  /** État d'erreur : pose la bordure danger + aria-invalid (l'enveloppe Field le branche aussi). */
  invalid?: boolean;
}

/* Digits roll up when a number grows and down when it shrinks; `custom` hands the latest direction to digits already leaving. */
const digit = {
  enter: (direction: number) => ({ opacity: 0, y: `${direction * 0.6}em`, filter: `blur(${motionTokens.blur.subtle}px)` }),
  center: { opacity: 1, y: 0, filter: "blur(0px)" },
  exit: (direction: number) => ({ opacity: 0, y: `${direction * -0.6}em`, filter: `blur(${motionTokens.blur.subtle}px)` }),
};
const isNumber = (word: string) => /^\d[\d.,/:]*%?$/.test(word);

/** A count in the copy, such as "12 characters", rolls only the digits that changed; a gained or lost digit opens or closes its width. */
function RollingNumber({ word }: { word: string }) {
  const reduced = useReducedMotion();
  const [shown, setShown] = useState({ word, direction: 1 });
  if (shown.word !== word) setShown({ word, direction: parseFloat(word.replace(/,/g, "")) < parseFloat(shown.word.replace(/,/g, "")) ? -1 : 1 });
  const characters = word.split("");
  const transition = reduced ? { duration: 0 } : { y: motionTokens.spring.snappy, width: motionTokens.spring.morph, opacity: { duration: motionTokens.duration.fast }, filter: { duration: motionTokens.duration.fast } };
  return <AnimatePresence initial={false}>{characters.map((character, index) => <motion.span key={characters.length - index} className={styles.column} initial={{ width: 0, opacity: 0 }} animate={{ width: "auto", opacity: 1 }} exit={{ width: 0, opacity: 0 }} transition={transition}>
    <AnimatePresence initial={false} mode="popLayout" custom={shown.direction}><motion.span key={character} custom={shown.direction} variants={digit} initial="enter" animate="center" exit="exit" transition={transition}>{character}</motion.span></AnimatePresence>
  </motion.span>)}</AnimatePresence>;
}

/** Changed words rise in and unblur while unchanged words hold still, and numbers roll. Assistive tech reads the plain copy. */
function MotionText({ text }: { text: string }) {
  const reduced = useReducedMotion();
  const words = text.split(" ");
  return <><span className={styles.srOnly}>{text}</span><span className={styles.words} aria-hidden="true"><AnimatePresence initial={false} mode="popLayout">{words.map((word, index) => <motion.span key={`${index}:${isNumber(word) ? "#" : word}`} className={styles.word}
    initial={reduced ? { opacity: 0 } : { opacity: 0, y: "0.35em", filter: `blur(${motionTokens.blur.soft}px)` }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    exit={reduced ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0, y: "-0.35em", filter: `blur(${motionTokens.blur.subtle}px)`, transition: { duration: motionTokens.duration.fast, ease: [...motionTokens.ease.standard] } }}
    transition={reduced ? { duration: motionTokens.duration.instant } : { duration: motionTokens.duration.standard, ease: [...motionTokens.ease.enter] }}>{isNumber(word) ? <RollingNumber word={word} /> : word}{index < words.length - 1 ? " " : null}</motion.span>)}</AnimatePresence></span></>;
}

/** Helper and error copy: the row opens its height on a spring, then the words settle in. */
function FieldMessage({ id, text, className, alert }: { id?: string; text?: string; className: string; alert?: boolean }) {
  return <AnimatePresence initial={false}>{text ? <MessageRow key="message" id={id} text={text} className={className} alert={alert} /> : null}</AnimatePresence>;
}

/** The row tracks the measured copy, so a longer message that wraps opens its next line instead of snapping. */
function MessageRow({ id, text, className, alert }: { id?: string; text: string; className: string; alert?: boolean }) {
  const reduced = useReducedMotion();
  const copyRef = useRef<HTMLSpanElement>(null);
  const [height, setHeight] = useState<number | "auto">("auto");
  useEffect(() => {
    const node = copyRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => setHeight(entry!.borderBoxSize?.[0]?.blockSize ?? node.offsetHeight));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return <motion.span className={styles.messageSlot} initial={reduced ? false : { height: 0, opacity: 0 }} animate={{ height, opacity: 1 }} exit={{ height: 0, opacity: 0, transition: reduced ? { duration: 0 } : { height: motionTokens.spring.smooth, opacity: { duration: motionTokens.duration.instant } } }} transition={reduced ? { duration: 0 } : { height: motionTokens.spring.smooth, opacity: { duration: motionTokens.duration.fast } }}>
    <motion.span ref={copyRef} id={id} className={className} role={alert ? "alert" : undefined} initial={reduced ? false : { y: "0.35em", filter: `blur(${motionTokens.blur.soft}px)` }} animate={{ y: 0, filter: "blur(0px)" }} transition={{ duration: reduced ? 0 : motionTokens.duration.standard, ease: [...motionTokens.ease.enter] }}><MotionText text={text} /></motion.span>
  </motion.span>;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, description, error, id, className, size, invalid, "aria-invalid": ariaInvalid, ...props }, ref,
) {
  const generatedId = useId();
  const wrapped = label != null || description != null || error != null;
  const controlId = id ?? (wrapped ? generatedId : undefined);
  const hintId = description ? `${controlId}-description` : undefined; const errorId = error ? `${controlId}-error` : undefined;
  const control = <textarea {...props} id={controlId} ref={ref} data-ubm-canon="" data-size={size ?? "md"} className={cn(textareaVariants({ size }), styles.control, className)} aria-invalid={error ? true : (ariaInvalid ?? (invalid ? true : undefined))} aria-describedby={[props["aria-describedby"], hintId, errorId].filter(Boolean).join(" ") || undefined}/>;
  if (!wrapped) return control;
  return <div className={styles.field}>{label != null ? <label htmlFor={controlId}>{label}</label> : null}{control}<FieldMessage id={hintId} text={description} className={styles.hint!} /><FieldMessage id={errorId} text={error} className={styles.error!} alert /></div>;
});

Textarea.displayName = "Textarea";

export { textareaVariants };

export default Textarea;
